import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { CHIP_CATEGORIES } from './ObservationsCandidatsPanel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { categorie: 'positif', libelle: '', phrase: '', alerte: false, actif: true };

export const ChipsObservationAdmin = () => {
  const [chips, setChips] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => axios.get(`${API_URL}/listes/chips-observation`).then(r => setChips(r.data)).catch(() => toast.error('Erreur lors du chargement')), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.libelle.trim() || !editing.phrase.trim()) { toast.error('Libellé et phrase obligatoires'); return; }
    setSaving(true);
    try {
      if (editing.id) await axios.put(`${API_URL}/listes/chips-observation/${editing.id}`, editing);
      else await axios.post(`${API_URL}/listes/chips-observation`, editing);
      toast.success('Chip enregistrée'); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  const remove = async (chip) => {
    if (!window.confirm(`Supprimer la chip « ${chip.libelle} » ?`)) return;
    try { await axios.delete(`${API_URL}/listes/chips-observation/${chip.id}`); toast.success('Chip supprimée'); load(); } catch { toast.error('Erreur'); }
  };

  const move = async (cat, list, i, dir) => {
    const ids = list.map(c => c.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    await axios.put(`${API_URL}/listes/chips-observation/reorder`, ids);
    load();
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="chips-observation-admin">
      <CardContent className="p-4 space-y-5">
        <div className="flex items-center gap-3">
          <p className="text-xs text-slate-500 flex-1">Phrases insérées d'un clic par les formateurs dans les observations du cahier de texte. Les chips « alerte » créent automatiquement une tâche pour le responsable pédagogique.</p>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" onClick={() => setEditing({ ...EMPTY })} data-testid="btn-add-chip"><Plus className="w-4 h-4" /> Ajouter</Button>
        </div>
        {CHIP_CATEGORIES.map(cat => {
          const list = chips.filter(c => c.categorie === cat.key).sort((a, b) => a.ordre - b.ordre);
          return (
            <div key={cat.key} data-testid={`chips-cat-${cat.key}`}>
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-2"><span className={`w-2.5 h-2.5 rounded-full ${cat.dot}`} /> {cat.label} <span className="text-xs text-slate-400 font-normal">({list.length})</span></p>
              <div className="border rounded-lg divide-y">
                {list.map((c, i) => (
                  <div key={c.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${c.actif === false ? 'opacity-50' : ''}`} data-testid={`chip-row-${c.id}`}>
                    <span className={`text-xs px-2.5 py-1 rounded-full border shrink-0 ${cat.chip}`}>{c.libelle}</span>
                    <span className="flex-1 text-slate-600 truncate" title={c.phrase}>{c.phrase}</span>
                    {c.alerte && <span className="flex items-center gap-1 text-[10px] text-red-600 font-medium shrink-0"><Bell className="w-3 h-3" /> tâche RP</span>}
                    {c.actif === false && <span className="text-[10px] text-slate-400 shrink-0">inactive</span>}
                    <div className="flex items-center shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(cat.key, list, i, -1)} data-testid={`chip-up-${c.id}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === list.length - 1} onClick={() => move(cat.key, list, i, 1)} data-testid={`chip-down-${c.id}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing({ ...c })} data-testid={`chip-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => remove(c)} data-testid={`chip-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
                {list.length === 0 && <p className="text-xs text-slate-400 px-3 py-3">Aucune chip</p>}
              </div>
            </div>
          );
        })}
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="chip-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? 'Modifier la chip' : 'Nouvelle chip'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Catégorie</Label>
                <Select value={editing.categorie} onValueChange={(v) => setEditing({ ...editing, categorie: v })}>
                  <SelectTrigger data-testid="chip-categorie-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHIP_CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Libellé (court, affiché sur la chip)</Label><Input value={editing.libelle} onChange={(e) => setEditing({ ...editing, libelle: e.target.value })} data-testid="chip-libelle-input" /></div>
              <div><Label>Phrase insérée dans l'observation</Label><Textarea rows={3} value={editing.phrase} onChange={(e) => setEditing({ ...editing, phrase: e.target.value })} data-testid="chip-phrase-input" /></div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div><p className="text-sm font-medium">Créer une tâche pour le responsable pédagogique</p><p className="text-xs text-slate-500">Dès qu'un formateur insère cette phrase dans une observation</p></div>
                <Switch checked={!!editing.alerte} onCheckedChange={(v) => setEditing({ ...editing, alerte: v })} data-testid="chip-alerte-switch" />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <p className="text-sm font-medium">Active (visible par les formateurs)</p>
                <Switch checked={editing.actif !== false} onCheckedChange={(v) => setEditing({ ...editing, actif: v })} data-testid="chip-actif-switch" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="chip-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
